import { Spinner } from "@magnetic/spinner";

import { ButtonScope, EditButton } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { StatusIcon } from "src/components/icons";
import { Text } from "src/components/text";
import { VFlex } from "src/components/vFlex";
import { Constants } from "src/utils/constants";

import { BaseColumnConfig } from "./baseColumnConfig";

export class StatusColumn extends BaseColumnConfig<boolean> {
  compare(a: boolean = false, b: boolean = false) {
    return Number(a) - Number(b);
  }
  render(data: boolean = false) {
    return (
      <HFlex align="center" gap="xxs">
        <StatusIcon status={data ? "positive" : "disabled"} />
        {data ? "Enabled" : "Disabled"}
      </HFlex>
    );
  }
}

type FirmwareUpgradeStatusData = {
  hasTargetVersion: boolean;
  isDownloaded: boolean;
  rowId: string;
  rowIndex: number;
  upgradeStatuses: Record<
    string,
    "in_progress" | "queued" | "completed" | "error"
  >;
  errorMessages: Record<string, string>;
  onUpgradeSingle: (rowId: string) => void;
};

export class FirmwareUpgradeStatusColumn extends BaseColumnConfig<FirmwareUpgradeStatusData> {
  constructor() {
    super({
      label: "Upgrade status",
      rowIdentifier: false,
      pinned: "right",
      sortable: false,
      agGridColumnDefinition: {
        suppressAutoSize: true,
        resizable: false,
        autoHeight: false,
        flex: 0,
        cellClass: "firmware-info-column"
      }
    });
  }

  identity() {
    return undefined;
  }

  compare() {
    return 0;
  }

  render(value: FirmwareUpgradeStatusData) {
    const {
      hasTargetVersion,
      isDownloaded,
      rowId,
      rowIndex,
      upgradeStatuses,
      errorMessages,
      onUpgradeSingle
    } = value;

    const status = upgradeStatuses[rowId];

    if (!hasTargetVersion || !isDownloaded) {
      return <Text>{Constants.EMPTY_VALUE}</Text>;
    }

    if (status === "in_progress" || status === "queued") {
      return (
        <HFlex align="center" gap="xxs">
          <Spinner size="xs" />
          <Text size="p4">
            {status === "in_progress"
              ? "Upgrade in progress"
              : "Upgrade queued"}
          </Text>
        </HFlex>
      );
    }

    if (status === "error") {
      return (
        <VFlex gap="none">
          <HFlex align="center" gap="xxs">
            <StatusIcon status="negative" size="sm" />
            <Text size="p4">Upgrade failed</Text>
          </HFlex>
          <Text size="p4" color="gray">
            {errorMessages[rowId] || "Unknown error"}
          </Text>
        </VFlex>
      );
    }

    return (
      <EditButton
        scope={ButtonScope.writeAccess}
        name={`upgrade-${rowIndex}`}
        kind="tertiary"
        size="sm"
        onClick={() => onUpgradeSingle(rowId)}
      >
        Upgrade pluggable
      </EditButton>
    );
  }
}
